import {
  rdbV1FiltersTeamsList,
  rdbV1FiltersYearsList,
  rdbV1OrganizationsRosterDetail,
  rdbV1OrganizationsRosterHeaderDetail,
} from '@on3/api';
import type { ITeamRosterProps } from '@on3/ui-lib/src/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TeamRosterList } from '@public/components/College/Roster/TeamRosterList';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { createDescription, createTitle } from '@public/utils/meta';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

const TeamRosterIndex = (props: ITeamRosterProps) => {
  const { currentSite } = props?.siteData || {};
  const router = useRouter();
  const { year, sport } = router.query;
  const orgName =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const orgMascot = currentSite?.organization?.mascot;
  const title = createTitle(year, orgName, orgMascot, sport, 'roster');
  const description = createDescription(
    '{0} {1} {2} {3} team roster.',
    year,
    orgName,
    orgMascot,
    sport,
  );
  const noIndex = (props?.rosterList?.pagination?.count || 0) === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <AdSidebarLayout>
        <TeamRosterList
          {...props}
          key={`${year}-${sport}-${orgName}`}
          title={title}
        />
      </AdSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps<
  ITeamRosterProps | []
> = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const orgKey = siteData?.currentSite?.organizationKey;
  const { year, sport } = ctx.query;

  const { sportsData } = siteData || {};
  const sportKey = sportsData?.find(
    (sp) => sp?.name && slugify(sp?.name) === sport,
  )?.key;

  if (!orgKey || !year || !sport || !sportKey) {
    return {
      notFound: true,
    };
  }

  const params = {
    sportKey,
    year: +year,
    organizationKey: orgKey,
  };
  const teamFilterParams = {
    sportKey,
    year: params.year,
    groupBy: true,
  };

  try {
    const [rosterList, rosterHeader, teams, years] = await Promise.allSettled([
      rdbV1OrganizationsRosterDetail(proxyApi, params),
      rdbV1OrganizationsRosterHeaderDetail(proxyApi, params),
      rdbV1FiltersTeamsList(proxyApi, teamFilterParams),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData,
      rosterList: getFulfilledValue(rosterList),
      rosterHeader: getFulfilledValue(rosterHeader),
      teams: getFulfilledValue(teams),
      years: getFulfilledValue(years),
    } as ITeamRosterProps;

    return { props: { ...data } };
  } catch (e) {
    console.log(e);

    return { props: [] };
  }
};

export default withValidUrl(TeamRosterIndex);
